import { useRef, useState } from "react"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { zIndex } from "@nova/constants"
import { Notification } from "@nova/schemas"
import { NotificationCard } from "./NotificationCard"
import { useMarkNotificationRead } from "./NotificationQueries"

const AUTO_DISMISS_MS = 5000
const EXIT_DURATION_MS = 300
const HOVER_READ_MS = 0

export const NotificationToast = ({
    notification,
    onDismiss,
}: {
    notification: Notification
    onDismiss: () => void
}) => {
    const { markNotificationRead } = useMarkNotificationRead()
    const [readAt, setReadAt] = useState(notification.readAt)
    const [leaving, setLeaving] = useState(false)
    const timer = useRef<number | undefined>(undefined)

    const setTimer = (action: () => void, ms: number) => {
        clearTimeout(timer.current)
        timer.current = window.setTimeout(action, ms)
    }

    const close = () => {
        setLeaving(true)
        setTimer(onDismiss, EXIT_DURATION_MS)
    }

    const markRead = () => {
        if (readAt) return
        setReadAt(new Date().toISOString())
        markNotificationRead(notification.id)
    }

    useEffectUnsafe(() => {
        setTimer(close, AUTO_DISMISS_MS)
        return () => clearTimeout(timer.current)
    }, [])

    return (
        <div
            role="status"
            aria-live="polite"
            className={classNames(
                "group fixed top-3.5 right-3.5 w-[380px] rounded-xl border border-white-200 bg-black-900 backdrop-blur-sm shadow-card",
                "animate-toast-in transition-all duration-300 ease-out",
                leaving && "translate-x-[120%] opacity-0"
            )}
            style={{ zIndex: zIndex.GlobalNotification }}
            onMouseEnter={() => !leaving && setTimer(markRead, HOVER_READ_MS)}
            onMouseLeave={() => !leaving && setTimer(close, AUTO_DISMISS_MS)}
            onFocus={() => !leaving && setTimer(markRead, HOVER_READ_MS)}
            onBlur={() => !leaving && setTimer(close, AUTO_DISMISS_MS)}
        >
            <div className="p-1">
                <NotificationCard notification={{ ...notification, readAt }} />
            </div>
            <button
                type="button"
                aria-label="Dismiss"
                className="absolute -top-3 -right-3 hidden group-hover:flex group-focus-within:flex items-center justify-center p-1 rounded-full bg-black-900 border border-gray-700"
                onClick={close}
            >
                <Icons.ExtraSmallIcon
                    Icon={Icons.XClose}
                    className="text-white-400 hover:text-white-700 cursor-pointer"
                />
            </button>
        </div>
    )
}
